import { AppDrawer } from '@/components/commons/appDrawer/appDrawer'
import { InputNumberStyled } from '@/components/commons/inputNumber'
import { ServicesVariantPickerDrawer } from '@/components/commons/servicesPicker/servicesVariantPickerDrawer'
import { useGetCategories } from '@/hooks/category/useGetCategories'
import { useCreateBundle } from '@/hooks/service/useCreateBundle'
import { useGetBundle } from '@/hooks/service/useGetBundle'
import { useGetServices } from '@/hooks/service/useGetServices'
import { useUpdateBundle } from '@/hooks/service/useUpdateBundle'
import { BUNDLE_PRICING_TYPE, ServiceItemData } from '@/types/bundle'

import {
  calculatePropotionalDiscount,
  getServiceWithPrice,
  getTotalPriceBundle,
} from '@/utils/helpers/bundleService'
import { formatDurations } from '@/utils/helpers/formatHelper'
import { formatMoneyRMG } from '@/utils/helpers/inputHelper'
import { PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  Divider,
  Drawer,
  DrawerProps,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Typography,
} from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const ServiceItem = ({ data }: { data: ServiceItemData }) => {
  const { t } = useTranslation()
  return (
    <div
      className="mb-2 border-0 border-l-4 border-solid rounded shadow"
      style={{ borderColor: data.color }}
    >
      <div className="flex gap-2 px-4 py-2 border border-l-0 border-gray-200 border-solid rounded">
        <div className="flex-1">
          <Typography.Text className="block">{data.name}</Typography.Text>
          <Typography.Text type="secondary">
            {t('common.duration')}: {formatDurations(data.duration)}
          </Typography.Text>
        </div>
        <div className="flex flex-col items-end">
          {data.price !== data.priceDiscount && (
            <Typography.Text type="secondary" className="line-through">
              {formatMoneyRMG(data.price, 'Rp')}
            </Typography.Text>
          )}
          <Typography.Text className="block font-semibold">
            {formatMoneyRMG(data.priceDiscount, 'Rp')}
          </Typography.Text>
        </div>
      </div>
    </div>
  )
}
const formId = 'bundle-form'
interface BundleFormDrawerProps extends DrawerProps {
  onClose: () => void
  bundleId?: string
}
export const BundleFormDrawer = ({
  bundleId,
  ...props
}: BundleFormDrawerProps) => {
  const { t } = useTranslation('catalog')
  const bundlePricingTypeOptions = useMemo(() => {
    return [
      {
        label: t('catalog.bundle.type.percent'),
        value: BUNDLE_PRICING_TYPE.Values.PERCENTAGE,
      },
      {
        label: t('catalog.bundle.type.fix'),
        value: BUNDLE_PRICING_TYPE.Values.FIX,
      },
    ]
  }, [t])
  const [form] = Form.useForm()
  const { data: bundle, isFetching } = useGetBundle({
    id: bundleId,
    enabled: !!props.open,
  })
  const { mutateAsync: createBundle, isPending: pendingAdd } = useCreateBundle()
  const { mutateAsync: updateBundle, isPending: pendingUpdate } =
    useUpdateBundle()
  const loadingAction = pendingAdd || pendingUpdate
  const [errNoService, setErrNoService] = useState(false)
  const isEnable = Form.useWatch('allowOnlineBooking', form)
  const pricingType = Form.useWatch('pricingType', form)
  const percentage = Form.useWatch('percentage', form) as number
  const fixPrice = Form.useWatch('fixPrice', form) as number
  const [selectedServices, setSelectedServices] = useState<
    { serviceId: string; variantId: string }[]
  >([])
  const { data: categories } = useGetCategories({ branch: '*' })

  const { data: services } = useGetServices({ branch: '*', params: {} })
  const [openSelectServices, setOpenSelectServices] = useState(false)
  useEffect(() => {
    if (props.open) {
      setErrNoService(false)
      form.resetFields()
      setSelectedServices([])
      if (bundleId && bundle) {
        setSelectedServices(bundle.services)
        form.setFieldsValue({
          allowOnlineBooking: bundle.allowOnlineBooking,
          pricingType: bundle.pricingType,
          percentage: bundle.percentage,
          fixPrice: bundle.fixPrice,
          name: bundle.name,
          description: bundle.description,
        })
      }
    }
  }, [bundle, bundleId, form, props.open])
  const serviceWithPrice = useMemo(() => {
    return getServiceWithPrice({
      bundleItems: selectedServices,
      categories,
      services,
    })
  }, [categories, selectedServices, services])

  const totalServices = useMemo(() => {
    return getTotalPriceBundle({
      items: serviceWithPrice,
      percentage,
      fixPrice,
      pricingType,
    })
  }, [fixPrice, percentage, pricingType, serviceWithPrice])
  const formatSelectedService = useMemo(() => {
    const items: ServiceItemData[] = []
    if (pricingType === BUNDLE_PRICING_TYPE.Values.PERCENTAGE) {
      return serviceWithPrice.map((el) => {
        const priceDiscount = (el.price / 100) * percentage || 0
        return {
          ...el,
          priceDiscount: el.price - priceDiscount,
        }
      })
    } else if (pricingType === BUNDLE_PRICING_TYPE.Values.FIX) {
      return calculatePropotionalDiscount({
        totalOriginPrice: totalServices.totalOriginPrice,
        totalPrice: fixPrice,
        items: serviceWithPrice,
      })
    }

    return items
  }, [
    fixPrice,
    percentage,
    pricingType,
    serviceWithPrice,
    totalServices.totalOriginPrice,
  ])
  const handleSubmitForm = (formvalues: {
    allowOnlineBooking: boolean
    description?: string
    name: string
    percentage?: number
    pricingType: BUNDLE_PRICING_TYPE
    fixPrice?: number
  }) => {
    if (!serviceWithPrice.length || serviceWithPrice.length < 2) {
      return
    }
    if (bundleId) {
      updateBundle({
        id: bundleId,
        payload: {
          ...formvalues,
          fixPrice: formvalues.fixPrice || 0,
          percentage: formvalues.percentage || 0,
          services: serviceWithPrice.map((el, i) => ({
            ...el,
            sequence: i + 1,
          })),
          description: formvalues.description || '',
        },
      }).then(() => {
        props.onClose()
      })
      return
    }
    createBundle({
      payload: {
        ...formvalues,
        fixPrice: formvalues.fixPrice || 0,
        percentage: formvalues.percentage || 0,
        services: serviceWithPrice.map((el, i) => ({ ...el, sequence: i + 1 })),
        description: formvalues.description || '',
      },
    }).then(() => {
      props.onClose()
    })
  }
  return (
    <>
      <ServicesVariantPickerDrawer
        open={openSelectServices}
        selectedServicesVariant={selectedServices}
        onApply={(srvs) => {
          setSelectedServices(srvs)
          setOpenSelectServices(false)
          setErrNoService(false)
        }}
        onClose={() => {
          setOpenSelectServices(false)
        }}
      />
      <AppDrawer
        loading={isFetching}
        footer={
          <Button
            disabled={loadingAction}
            loading={loadingAction}
            block
            form={formId}
            htmlType="submit"
            type="primary"
          >
            {bundleId ? t('common.actions.update') : t('common.actions.save')}
          </Button>
        }
        title={bundleId ? t('catalog.edit.bundle') : t('catalog.add.bundle')}
        size={500}
        {...props}
      >
        <Form
          onSubmitCapture={() => {
            if (!serviceWithPrice.length || serviceWithPrice.length < 2) {
              setErrNoService(true)
              return
            }
          }}
          form={form}
          initialValues={{
            allowOnlineBooking: false,
            pricingType: BUNDLE_PRICING_TYPE.Values.PERCENTAGE,
          }}
          id={formId}
          onFinish={handleSubmitForm}
          layout="vertical"
        >
          <Typography.Title level={5}>
            {t('catalog.bundle.info')}
          </Typography.Title>
          <Form.Item
            rules={[{ required: true }]}
            name="name"
            label={t('common.name')}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label={t('common.description')}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Divider />
          <Typography.Title
            level={5}
            className="mt-0"
            style={{ marginBottom: 0 }}
          >
            {t('menu.services')}{' '}
            <span className="text-red-500" style={{ fontWeight: 300 }}>
              *
            </span>
          </Typography.Title>
          <Typography.Paragraph type="secondary">
            {t('catalog.bundle.services')}
          </Typography.Paragraph>
          {formatSelectedService.map((el) => (
            <ServiceItem data={el} key={el.variantId} />
          ))}
          <div className="my-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => setOpenSelectServices(true)}
                size="middle"
                icon={<PlusOutlined />}
                type="primary"
                ghost
              >
                {t('common.add.service')}
              </Button>

              <div className="flex flex-col items-end">
                {totalServices.totalOriginPrice !==
                  totalServices.totalPriceAfterDiscount && (
                  <Typography.Text type="secondary" className="line-through">
                    {formatMoneyRMG(totalServices.totalOriginPrice, 'Rp')}
                  </Typography.Text>
                )}
                <Typography.Text className="block font-semibold">
                  {formatMoneyRMG(totalServices.totalPriceAfterDiscount, 'Rp')}
                </Typography.Text>
              </div>
            </div>
            {!!errNoService && (
              <Typography.Text type="danger">
                {t('catalog.bundle.services.min')}
              </Typography.Text>
            )}
          </div>
          <Divider />
          <Typography.Title level={5}>
            {t('catalog.bundle.pricing')}
          </Typography.Title>
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <Form.Item
                name="pricingType"
                label={t('catalog.bundle.pricing.type')}
              >
                <Select options={bundlePricingTypeOptions} />
              </Form.Item>
            </Col>
            <Col span={12}>
              {pricingType === BUNDLE_PRICING_TYPE.Values.PERCENTAGE ? (
                <Form.Item
                  label={t('common.discount')}
                  name="percentage"
                  rules={[{ required: true }]}
                >
                  <InputNumberStyled suffix="%" className="w-full" max={100} />
                </Form.Item>
              ) : (
                <Form.Item
                  label={t('common.price')}
                  name="fixPrice"
                  rules={[{ required: true }]}
                >
                  <InputNumberStyled
                    formatter={(val) => formatMoneyRMG(val || '') as string}
                    prefix="Rp"
                    min={1}
                    max={totalServices.totalOriginPrice}
                    className="w-full"
                  />
                </Form.Item>
              )}
            </Col>
          </Row>
          <Typography.Title
            level={5}
            className="mt-0"
            style={{ marginBottom: 0 }}
          >
            {t('common.online.book')}
          </Typography.Title>
          <Typography.Paragraph type="secondary">
            {t('catalog.service.online.book.desc')}
          </Typography.Paragraph>
          <div className="flex items-center">
            <Form.Item
              name="allowOnlineBooking"
              valuePropName="checked"
              className="mb-0"
            >
              <Switch />
            </Form.Item>
            <Typography.Text className="ml-2">
              {isEnable
                ? t('catalog.service.online.book.enabled')
                : t('catalog.service.online.book.disabled')}
            </Typography.Text>
          </div>
        </Form>
      </AppDrawer>
    </>
  )
}
